import { Component, ViewChild } from '@angular/core';
import { DriverReferenceService } from '../driver-reference.service';
import { AutocompleteCellComponent } from '../../../shared/grid/autocomplete-cell.component';
import { DatepickerCellComponent } from '../../../shared/grid/datepicker-cell.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ValueSetterParams, GridReadyEvent, ColumnApi, GridApi, CellContextMenuEvent, Column, ColDef } from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { BulkActionDialogComponent } from '../../../shared/grid/bulk-action-dialog.component';
import { zip, Observable, Subject } from 'rxjs';
import { SelectAllHeaderComponent } from '../../../shared/grid/select-all-header.component';
import { DriverReference } from '../driver-reference';
import { Preferences } from '../../../shared/preferences/preferences';
import { AuthenticationService, NoResultsComponent } from '../../../shared';
import { NotificationsService, NotificationType, Notification } from '../../../shared/notification/notifications.service';
import { PreferencesService } from '../../../shared/preferences/preferences.service';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { debounce } from 'lodash';
import shortid = require('shortid');
import { DriverReferenceCreateModalComponent } from '../driver-reference-create-modal/driver-reference-create-modal.component';
import { ActionMenuOption } from '../../../../app/shared';

/* eslint-disable @typescript-eslint/naming-convention */
enum ActionType {
  ResetFilters = 'Reset Filters'
}

@Component({
  selector: 'driver-reference-grid',
  templateUrl: './driver-reference-grid.component.html',
  styleUrls: ['../../../../style/grid.scss', './driver-reference-grid.component.scss'],
  providers: [
    DriverReferenceService,
    NotificationsService,
    PreferencesService,
  ],
})
export class DriverReferenceGridComponent {
  @ViewChild('driversGrid', {static: true}) driversGrid!: AgGridAngular;
  columnApi!: ColumnApi;
  gridApi!: GridApi;

  enabledFeatures: string[] = this.authenticationService.enabledFeatures() || [];
  hasReadOnlyTicketAccess = this.enabledFeatures.includes('hasReadonlyTicketAccess');

  frameworkComponents = {
    autocompleteCell: AutocompleteCellComponent,
    datepickerCell: DatepickerCellComponent,
    selectAllHeader: SelectAllHeaderComponent,
    customNoRowsOverlay: NoResultsComponent,
  };

  noRowsOverlayComponent = 'customNoRowsOverlay';

  noRowsOverlayComponentParams = {
    type: 'driver references',
    onClick: () => this.onAddDriverClick(),
    readOnly: this.hasReadOnlyTicketAccess
  };

  sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel'
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ]
  };

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true
  };

  columnDefs = [
    {
      headerName: 'Select All',
      field: 'select',
      headerComponent: 'selectAllHeader',
      pinned: 'left',
      width: 70,
      editable: false,
      checkboxSelection: true,
      headerComponentParams: {
        checkboxSelection: true,
        service: this.driverReferenceService,
        selected: this.driverReferenceService.allSelected
      }
    },
    {
      headerName: 'Driver Code',
      field: 'driverCode',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: any) => {
          this.driverReferenceService.getValuesForFieldQuery('driverCode').subscribe(fields => {
            fields.push('-- Blank --');
            params.success(fields);
          });
        },
        newRowsAction: 'keep',
        buttons: ['reset']
      },
      editable: !this.hasReadOnlyTicketAccess,
      cellEditor: 'autocompleteCell',
      cellEditorParams: {
        service: this.driverReferenceService,
      },
      onCellValueChanged: (e: ValueSetterParams) => this.onCellValueChanged(e),
    },
    {
      headerName: 'Driver Name',
      field: 'driverName',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: any) => {
          this.driverReferenceService.getValuesForFieldQuery('driverName').subscribe(fields => {
            fields.push('-- Blank --');
            params.success(fields);
          });
        },
        newRowsAction: 'keep',
        buttons: ['reset']
      },
      editable: !this.hasReadOnlyTicketAccess,
      cellEditor: 'autocompleteCell',
      cellEditorParams: {
        service: this.driverReferenceService,
      },
      onCellValueChanged: (e: ValueSetterParams) => this.onCellValueChanged(e),
    },
    {
      headerName: 'Carrier',
      field: 'carrierName',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: any) => {
          this.driverReferenceService.getValuesForFieldQuery('carrierName').subscribe(fields => {
            fields.push('-- Blank --');
            params.success(fields);
          });
        },
        newRowsAction: 'keep',
        buttons: ['reset']
      },
      editable: !this.hasReadOnlyTicketAccess,
      cellEditor: 'autocompleteCell',
      cellEditorParams: {
        service: this.driverReferenceService,
      },
      onCellValueChanged: (e: ValueSetterParams) => this.onCellValueChanged(e),
    },
    {
      headerName: 'Email Address',
      field: 'email',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: any) => {
          this.driverReferenceService.getValuesForFieldQuery('email').subscribe(fields => {
            fields.push('-- Blank --');
            params.success(fields);
          });
        },
        newRowsAction: 'keep',
        buttons: ['reset']
      },
      editable: !this.hasReadOnlyTicketAccess,
      cellEditor: 'autocompleteCell',
      cellEditorParams: {
        service: this.driverReferenceService,
      },
      onCellValueChanged: (e: ValueSetterParams) => this.onCellValueChanged(e),
    },
    {
      headerName: 'Phone Number',
      field: 'phoneNumber',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: any) => {
          this.driverReferenceService.getValuesForFieldQuery('phoneNumber').subscribe(fields => {
            fields.push('-- Blank --');
            params.success(fields);
          });
        },
        newRowsAction: 'keep',
        buttons: ['reset']
      },
      editable: !this.hasReadOnlyTicketAccess,
      cellEditor: 'autocompleteCell',
      cellEditorParams: {
        service: this.driverReferenceService,
      },
      onCellValueChanged: (e: ValueSetterParams) => this.onCellValueChanged(e),
    },
    {
      headerName: 'CDL',
      field: 'cdl',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: (params: any) => {
          this.driverReferenceService.getValuesForFieldQuery('cdl').subscribe(fields => {
            fields.push('-- Blank --');
            params.success(fields);
          });
        },
        newRowsAction: 'keep',
        buttons: ['reset']
      },
      editable: !this.hasReadOnlyTicketAccess,
      cellEditor: 'autocompleteCell',
      cellEditorParams: {
        service: this.driverReferenceService,
      },
      onCellValueChanged: (e: ValueSetterParams) => this.onCellValueChanged(e),
    },
  ];

  rowModelType = 'serverSide';
  serverSideStoreType = 'partial';
  cacheBlockSize = 100;
  maxBlocksInCache = 10;

  loading = false;
  search = '';
  searchChanged: Subject<string> = new Subject<string>();

  preferences!: Preferences;
  user = this.authenticationService.user();

  driverFiltersString = localStorage.getItem('driverFilters') as string;
  driverFilters = this.driverFiltersString && this.driverFiltersString.length && this.driverFiltersString[0] === '{' ?
                  JSON.parse(this.driverFiltersString) : {};
  
  actionOptions: ActionMenuOption[] = [];

  constructor(
    private driverReferenceService: DriverReferenceService,
    private bulkDeleteDialog: MatDialog,
    private driverReferenceCreateModal: MatDialog,
    private authenticationService: AuthenticationService,
    private notificationsService: NotificationsService,
    private preferencesService: PreferencesService,
  ) {
    this.searchChanged.pipe(
      debounceTime(300), distinctUntilChanged()
    ).subscribe(search => {
      this.search = search;
      this.driverReferenceService.search = this.search;
      this.refreshTable();
    });
  }

  getContextMenuItems(params: any) {
    const menuItems: any[] = [];
    if (!this.hasReadOnlyTicketAccess) {
      menuItems.push({
        name: 'Delete',
        action: () => this.onDeleteDriverReference(params)
      });
    }
    menuItems.push({
      name: 'Export',
      subMenu: [
        {
          name: 'Export Selected',
          action: () => this.onExportSelected(params),
        },
        {
          name: 'Export All',
          action: () => this.onExportAll(),
        },
      ]
    });
    return menuItems;
  }

  onDeleteDriverReference(params: CellContextMenuEvent) {
    if (!params.api) {
      return;
    }

    let selected = params.api.getSelectedRows();
    if (!selected || !selected.length) {
      selected = [params.node.data];
      params.node.setSelected(true);
    }
    const dialog = this.bulkDeleteDialog.open(BulkActionDialogComponent, {
      width: '500px',
      data: {
        selected
      },
    });

    dialog.componentInstance.callback = () => {
      const jobs = zip(
        ...selected.map((driver: DriverReference) => this.deleteDriverReference(driver))
      );

      jobs.subscribe(() => {
        this.refreshTable(true);
      });
    };
  }

  deleteDriverReference(driver: DriverReference): Observable<any> {
    return this.driverReferenceService.remove(driver);
  }

  onExportSelected(params: any) {
    let selected: string[];

    selected = params.api ?
               params.api.getSelectedRows().map((s: any) => s.id) :
               params.map((s: any) => s.id);
    if ((!selected || !selected.length) && params.node && params.node.data) {
      selected = [params.node.data.id];
      params.node.setSelected(true);
    }

    this.driverReferenceService.exportGrid('drivers', selected).subscribe(() => {}, error => {
      const notification: Notification = {
        context: {
          error,
        },
        id: shortid.generate(),
        message: 'An error occured exporting your data.',
        originator: 'drivers',
        type: NotificationType.Danger,
      };

      this.notificationsService.addNotification(notification);
    });
  }

  onExportAll() {
    this.driverReferenceService.exportGrid('drivers').subscribe(() => {}, error => {
      const notification: Notification = {
        context: {
          error,
        },
        id: shortid.generate(),
        message: 'An error occured exporting your data.',
        originator: 'drivers',
        type: NotificationType.Danger,
      };

      this.notificationsService.addNotification(notification);
    });
  }

  refreshTable(clear = false): void {
    if (clear) {
      this.gridApi.deselectAll();
      this.gridApi.clearRangeSelection();
    }
    this.gridApi.refreshCells();
    this.gridApi.purgeServerSideCache();
  }

  onCellValueChanged(e: ValueSetterParams): void {
    const driver: DriverReference = { ...e.data };

    delete driver.classes;
    delete driver.createdAt;
    delete driver.loading;
    delete driver.selected;

    this.driverReferenceService.save(driver).subscribe();
  }

  onGridReady(e: GridReadyEvent): void {
    if (!e.columnApi || !e.api) {
      return;
    }

    this.columnApi = e.columnApi;
    this.gridApi = e.api;

    this.applyPreferences();

    this.driversGrid.gridOptions.getContextMenuItems = (params) => this.getContextMenuItems(params);

    e.api.setServerSideDatasource(this.driverReferenceService);

    this.setActionsMenu();
  }

  setActionsMenu() {
    this.actionOptions = this.actionOptions.concat([
      {
        name: ActionType.ResetFilters,
        onClick: () => this.gridApi.setFilterModel(null),
        disabled: () => false
      }
    ]);
  }

  onFilterChanges() {
    this.driverFilters = this.gridApi.getFilterModel();
    const filterKeys = Object.keys(this.driverFilters);
    filterKeys.forEach(key => {
      const obj = this.driverFilters[key];
      if(!obj.values || !obj.values.length) {
        delete this.driverFilters[key];
      }
    });
    localStorage.setItem('driverFilters', JSON.stringify(this.driverFilters));
  }

  onFirstDataRendered(): void {
    this.autoSizeAll();
  }

  autoSizeAll(): void {
    this.gridApi.sizeColumnsToFit();
  }

  onAddDriverClick(): void {
    const dialog = this.driverReferenceCreateModal.open(DriverReferenceCreateModalComponent, {
      width: '500px',
    });

    dialog.componentInstance.callback = (response$: Observable<DriverReference>) => {
      response$.subscribe(() => this.refreshTable());
    };
  }

  changeSearch(term?: string): void {
    this.searchChanged.next(term);
  }

  applyPreferences() {
    // Only update or create a default preference until UI for selection is in place
    this.preferencesService.list().pipe(
      map(prefs => {
        const pref = prefs.find(p => p.name === 'driverreference-default');

        return pref ? pref : new Preferences();
      }),
    ).subscribe(preferences => {
      this.preferences = preferences;

      if (preferences.type === 'driverreference' && preferences.blob) {
        this.columnApi.setColumnState(preferences.blob.columnState);
        this.gridApi.setSortModel(preferences.blob.sortState);
      }

      const eventList = [
        'gridColumnsChanged',
        'columnPinned',
        'columnVisible',
        'columnResized',
        'columnMoved'
      ];

      eventList.forEach(event => {
        this.gridApi.addEventListener(event, debounce(() => this.savePreferences(), 300));
      });
    }, error => {
      const notification: Notification = {
        context: {
          error,
        },
        id: shortid.generate(),
        message: 'An error occured saving your preferences.',
        originator: 'driverreferences',
        type: NotificationType.Danger,
      };

      this.notificationsService.addNotification(notification);
    });
  }

  savePreferences() {
    const columnData = this.columnApi.getColumnState();
    const preferencesObj = {
      ...this.preferences,
      profile: this.user.id,
      type: 'driverreference',
      name: 'driverreference-default',
      blob: {
        columnState: columnData,
      }
    };

    this.preferencesService.save(preferencesObj).subscribe(() => {}, error => {
      const notification: Notification = {
        context: {
          error,
        },
        id: shortid.generate(),
        message: 'An error occured saving your preferences.',
        originator: 'driverreferences',
        type: NotificationType.Danger,
      };

      this.notificationsService.addNotification(notification);
    });
  }

  getRowHeight() {
    return 48;
  }
}

